import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Code2, FileInput, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { ReportAssetsProvider } from '@/frontend/reports/primitives';
import { InputKind, InputSheet, InputValues, emptyInputs, sheetProgress } from '@/reports/inputs';
import { ReportBundleDiagnostic } from '@/reports/bundles/types';
import { INPUT_SHEET_FILE } from '@/reports/bundles/inputSheet';
import { insertInput, insertSection, insertSectionAt, locate, newInput, newSection } from '@/reports/bundles/sheetEdit';
import { cn } from '@/frontend/lib/utils';
import { Button } from '../../ui/button';
import { IconButton } from '../studioChrome';
import { FillContextProvider, focusSlot } from '../../reportsV2/fillContext';
import { BeforePublishCard, ProgressCard } from '../../reportsV2/ProgressRail';
import { SheetForm } from '../../reportsV2/SheetForm';
import { DRAG_NEW, DRAG_NEW_SECTION, KIND_ICONS, PALETTE, kindLabel } from './builderControls';
import { InputProperties } from './InputProperties';
import { DropTarget, InputsCanvas, Selection } from './InputsCanvas';
import { SweepBuilder } from './SweepBuilder';

export type InputsMode = 'design' | 'try';

/** The sheet in `inputs.json`, as far as it parses: an object with sections, even one the compiler would still refuse. */
export function readSheetFile(text: string | undefined): { sheet: InputSheet | null; error: string | null } {
  if (text === undefined) return { sheet: null, error: null };
  try {
    const raw = JSON.parse(text) as unknown;
    if (raw && typeof raw === 'object' && Array.isArray((raw as InputSheet).sections)) return { sheet: raw as InputSheet, error: null };
    return { sheet: null, error: I18n.get('rv2SheetNotSheet') };
  } catch (error) {
    return { sheet: null, error: error instanceof Error ? error.message : String(error) };
  }
}

export const writeSheetFile = (sheet: InputSheet) => `${JSON.stringify(sheet, null, 2)}\n`;

/**
 * The Inputs tab — the report studio's, and a schema's own page: the input
 * sheet designed as a form — a palette to add from, the sheet as the operator
 * sees it, and the selected input's properties — or tried out, filled in
 * exactly as the fill page fills it. A published version (`readOnly`) can only
 * be tried.
 */
export function InputsTab({ text, diagnostics, mode, readOnly, onChange, onOpenCode, tried, onTry, tryFooter }: {
  text: string | undefined;
  diagnostics: ReportBundleDiagnostic[];
  mode: InputsMode;
  readOnly?: boolean;
  onChange: (text: string) => void;
  onOpenCode?: () => void;
  /** what is typed in Try it, kept by the page so the preview can read it; kept here when not given */
  tried?: InputValues;
  onTry?: (entered: InputValues) => void;
  /** under the form in Try it: what the report comes to, and saving it as a fixture */
  tryFooter?: ReactNode;
}) {
  const { sheet, error } = useMemo(() => readSheetFile(text), [text]);
  const [selection, setSelection] = useState<Selection>(null);
  const [editingSweep, setEditingSweep] = useState<string | null>(null);
  const problems = diagnostics.filter((d) => d.file === INPUT_SHEET_FILE);

  if (text === undefined) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-[1.5rem] text-center">
        <FileInput className="size-6 text-muted-foreground" />
        <p className="font-medium">{I18n.get('rv2NoSheetTitle')}</p>
        <p className="max-w-md text-muted-foreground">{I18n.get('rv2NoSheetHelp')}</p>
      </div>
    );
  }
  if (!sheet) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-[0.75rem] p-[1.5rem] text-center">
        <AlertTriangle className="size-6 text-destructive" />
        <p className="max-w-md">{I18n.get('rv2SheetUnreadable', { error: error ?? '' })}</p>
        {onOpenCode && <Button variant="outline" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>}
      </div>
    );
  }

  const set = (next: InputSheet) => onChange(writeSheetFile(next));

  /** Add a new input (or section) where it was dropped, or below the selection. */
  const add = (kind: InputKind | 'section', at?: DropTarget) => {
    let next = sheet;
    if (kind === 'section') {
      const section = newSection(next);
      // dropped, `at.section` is the gap between sections it was dropped in
      if (at) set(insertSectionAt(next, section, at.section));
      else {
        const after = selection?.kind === 'section' ? selection.id : selection?.kind === 'input' ? next.sections[locate(next, selection.id)?.section ?? -1]?.id : undefined;
        set(insertSection(next, section, after));
      }
      setSelection({ kind: 'section', id: section.id });
      return;
    }
    if (next.sections.length === 0) next = insertSection(next, newSection(next, I18n.get('rv2FirstSection')));
    let target = at ?? { section: next.sections.length - 1, index: next.sections[next.sections.length - 1].inputs.length };
    if (!at && selection?.kind === 'input') {
      const found = locate(next, selection.id);
      if (found) target = { section: found.section, index: found.index + 1 };
    } else if (!at && selection?.kind === 'section') {
      const s = next.sections.findIndex((x) => x.id === selection.id);
      if (s >= 0) target = { section: s, index: next.sections[s].inputs.length };
    }
    const input = newInput(kind, next);
    set(insertInput(next, target.section, input, target.index));
    setSelection({ kind: 'input', id: input.id, naming: true });
    if (kind === 'sweep') setEditingSweep(input.id);
  };

  if (mode === 'try' || readOnly) return <TryIt sheet={sheet} entered={tried} onEntered={onTry} footer={tryFooter} />;

  // sized by its own width, not the window's: beside the designer's side bar and preview it is often narrow, and
  // then the properties open over the form while something is selected
  return (
    <div className="@container h-full min-h-0">
      <div className="relative grid h-full min-h-0 grid-cols-[11rem_minmax(0,1fr)] @4xl:grid-cols-[13rem_minmax(0,1fr)_19rem]">
        <aside className="min-h-0 overflow-y-auto border-r-[1px] p-[1rem]" aria-label={I18n.get('rv2AddAnInput')}>
          <h2 className="text-sm font-semibold">{I18n.get('rv2AddAnInput')}</h2>
          <p className="mb-[0.75rem] text-xs text-muted-foreground">{I18n.get('rv2AddAnInputHelp')}</p>
          {PALETTE.map(({ group, kinds }) => (
            <div key={group} className="mb-[0.75rem]">
              <p className="mb-1.5 text-[0.7rem] font-medium tracking-wide text-muted-foreground uppercase">{I18n.get(group)}</p>
              <div className="flex flex-col gap-1">
                {kinds.map((kind) => {
                  const Icon = KIND_ICONS[kind];
                  return (
                    <button key={kind} type="button" draggable onDragStart={(e) => { e.dataTransfer.setData(kind === 'section' ? DRAG_NEW_SECTION : DRAG_NEW, kind); e.dataTransfer.effectAllowed = 'copy'; }} onClick={() => add(kind)}
                      className="flex h-8 cursor-grab items-center gap-2 rounded-md border-[1px] bg-background px-2.5 text-left text-sm hover:bg-muted active:cursor-grabbing">
                      <Icon className="size-4 shrink-0 text-muted-foreground" /> {kindLabel(kind)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>
        <div className="min-h-0 overflow-y-auto bg-muted/40 px-[1.5rem] py-[1.25rem]" onClick={() => setSelection(null)}>
          <div className="mx-auto max-w-[48rem]">
            {problems.length > 0 && (
              <div role="alert" className="mb-[1rem] rounded-lg border-[1px] border-destructive/40 bg-destructive/5 px-[0.75rem] py-2 text-xs">
                <p className="mb-1 font-medium text-destructive">{I18n.get('rv2SheetProblems')}</p>
                <ul className="list-disc pl-4">{problems.slice(0, 6).map((p, i) => <li key={i}>{p.message}</li>)}</ul>
              </div>
            )}
            <InputsCanvas sheet={sheet} selection={selection} onSelect={setSelection} onChange={set} onDropNew={(kind, at) => add(kind, at)} />
          </div>
        </div>
        <aside className={cn('min-h-0 overflow-y-auto border-l-[1px] bg-background @4xl:static @4xl:block @4xl:w-auto @4xl:shadow-none', selection ? 'absolute inset-y-0 right-0 z-20 w-[19rem] shadow-lg' : 'hidden')} aria-label={I18n.get('rv2Properties')}>
          {selection && <IconButton label={I18n.get('designerCloseProperties')} size="icon-xs" className="absolute top-2 right-2 z-10 @4xl:hidden" onClick={() => setSelection(null)}><X /></IconButton>}
          <InputProperties sheet={sheet} selection={selection} onChange={set} onSelect={setSelection} onEditSweep={setEditingSweep} />
        </aside>
        {editingSweep && (
          <SweepBuilder sheet={sheet} sweepId={editingSweep} onCancel={() => setEditingSweep(null)} onSave={(next) => { set(next); setEditingSweep(null); }} />
        )}
      </div>
    </div>
  );
}

/** The sheet filled in as the fill page fills it, kept only here (or by the page); files stay in this browser. */
function TryIt({ sheet, entered: given, onEntered, footer }: { sheet: InputSheet; entered?: InputValues; onEntered?: (entered: InputValues) => void; footer?: ReactNode }) {
  const [own, setOwn] = useState<InputValues>({});
  const entered = given ?? own;
  const setEntered = (next: InputValues | ((current: InputValues) => InputValues)) => {
    if (onEntered) onEntered(typeof next === 'function' ? next(entered) : next);
    else setOwn(next);
  };
  const inputs = useMemo(() => ({ ...emptyInputs(sheet), ...entered }), [sheet, entered]);
  const progress = useMemo(() => sheetProgress(sheet, inputs), [sheet, inputs]);
  const local = useRef(new Map<string, string>());
  const fill = useMemo(() => ({
    editable: true,
    traces: {},
    upload: async (file: File) => {
      const fileId = `try-${local.current.size + 1}`;
      local.current.set(fileId, URL.createObjectURL(file));
      return { fileId, fileName: file.name };
    },
  }), []);
  const resolve = useCallback(async (ids: string[]) => Object.fromEntries(ids.flatMap((id) => (local.current.has(id) ? [[id, local.current.get(id) as string]] : []))), []);
  return (
    <FillContextProvider value={fill}>
      <ReportAssetsProvider resolve={resolve}>
        <div className="flex h-full flex-col">
          <div className="@container min-h-0 flex-1 overflow-y-auto bg-muted/40">
            <div className="mx-auto grid max-w-[80rem] grid-cols-1 gap-[1.25rem] px-[1.5rem] py-[1.25rem] @4xl:grid-cols-[17rem_minmax(0,1fr)]">
              <aside className="flex flex-col gap-[1rem] @4xl:sticky @4xl:top-0 @4xl:self-start">
                <ProgressCard progress={progress} />
                <BeforePublishCard blockers={progress.missing.map((slot) => ({ key: `${slot.input}:${slot.cell ?? ''}:${slot.field ?? ''}`, label: I18n.get(slot.cell === undefined ? 'rv2IsRequired' : 'rv2IsEmpty', { label: slot.label }), blocking: true, onSelect: () => focusSlot(slot) }))} />
                <div className="flex flex-col gap-1 px-1 text-xs text-muted-foreground">
                  <p>{I18n.get('rv2TryItNote')}</p>
                  <Button variant="ghost" size="sm" className="self-start" onClick={() => setEntered({})}>{I18n.get('rv2TryItReset')}</Button>
                </div>
              </aside>
              <main className="min-w-0"><SheetForm sheet={sheet} inputs={inputs} progress={progress.sections} onChange={(id, value) => setEntered((e) => ({ ...e, [id]: value }))} /></main>
            </div>
          </div>
          {footer}
        </div>
      </ReportAssetsProvider>
    </FillContextProvider>
  );
}
